import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Input,
  Button,
  Typography,
  Stack,
  Alert,
  Box,
  Link,
  Checkbox,
} from '@mui/joy';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useLoading } from '../../context/LoadingContext';
import AuthLayout from '../../components/Layout/AuthLayout';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { setLoading, isLoading } = useLoading();

  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading('login', true);

    try {
      const result = await login({ email, password, rememberMe });

      if (result.success) {
        showSuccess(`Welcome back, ${result.user.name}!`);
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Login failed');
        showError('Invalid credentials. Please try again.');
      }
    } catch (error) {
      setError('An error occurred during login');
      showError('Login failed. Please try again.');
    } finally {
      setLoading('login', false);
    }
  };

  return (
    <AuthLayout>
      <Card
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '400px',
          p: 4,
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          borderRadius: '16px',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img src="/logo-vector.svg" alt="Vector India" style={{ width: '250px' }} />

            <Typography level="h2" sx={{ color: 'primary.500', mb: 1, fontWeight: 600 }}>
              Digital Signage
            </Typography>
          </Box>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}

              <FormControl>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="lg"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  size="lg"
                  required
                  autoComplete="current-password"
                />
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Checkbox
                  label="Remember me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size="sm"
                />
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  level="body-sm"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                size="lg"
                loading={isLoading('login')}
                sx={{
                  background: 'linear-gradient(135deg, #eb7a24 0%, #f19b30 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d16000 0%, #e67700 100%)',
                  },
                  mt: 2,
                }}
              >
                Sign In
              </Button>
            </Stack>
          </form>

          {/* Footer */}
          <Box sx={{
            mt: 6,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'neutral.200',
            textAlign: 'center'
          }}>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              © {new Date().getFullYear()} Vector India. Digital Signage Solutions.
            </Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.400', mt: 1 }}>
              Need help? Contact your system administrator.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default LoginPage;